import './main.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';

import { ApiContext } from './api/api.context';
import { createAdminApi } from './api/api.endpoints';
import { ApiClient } from './api/apiClient';
import { ApiError } from './api/apiError';
import { I18nProvider } from './i18n/I18nProvider';
import { AppRoutes } from './routes';

const apiClient = new ApiClient({
    baseUrl: '/api'
});

const api = createAdminApi(apiClient);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Only retry on server errors / network failures. 4xx responses
            // (validation, auth, not-found) are deterministic and shouldn't
            // be hammered — the previous `retry: true` was issuing 3-4
            // duplicate requests on every form validation failure.
            retry: (failureCount, error) => {
                if (failureCount >= 3) return false;
                if (error instanceof ApiError) return error.status >= 500;
                return true; // network / fetch errors — worth a retry
            },
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
        }
    }
});

// Recover session from the httpOnly refresh cookie before first render so
// AuthGuard's synchronous `hasSession()` check doesn't wrongly redirect to
// /login on page reload. Routes on the public whitelist (login, magic-link,
// access-denied) skip the wait to avoid a pointless roundtrip for anonymous
// visitors.
const PUBLIC_PATHS = ['/login', '/auth/magic-link', '/access-denied'];
const shouldBootstrap = !PUBLIC_PATHS.includes(window.location.pathname);

async function start() {
    if (shouldBootstrap) {
        await apiClient.bootstrapSession();
    }

    createRoot(document.getElementById('app')!).render(
        <ApiContext.Provider value={{ api }}>
            <QueryClientProvider client={queryClient}>
                <I18nProvider>
                    <AppRoutes />
                    <Toaster />
                </I18nProvider>
            </QueryClientProvider>
        </ApiContext.Provider>
    );
}

start();
